// telegram.slice.js

import { createSlice } from '@reduxjs/toolkit';
import {
  getTelegramBotConfigs,
  addTelegramBotConfig,
  deleteTelegramBotConfig,
  getTelegramContacts,
  getTelegramMessages,
  sendTelegramMessage,
  resetUnreadCount,
} from './telegram-action';

import { DEFAULT_TELEGRAM_STATE } from '../../constants/telegram';

const telegramSlice = createSlice({
  name: 'telegram',
  initialState: DEFAULT_TELEGRAM_STATE,

  reducers: {
    setSelectedBotConfig(state, action) {
      state.selectedBotConfigId = action.payload;
      state.selectedContact = null;
      state.selectedChatId = null;
      state.messages = [];
      state.isUserSelected = false;
      state.contacts = [];
      state.page = DEFAULT_TELEGRAM_STATE.page;
      state.hasMore = true;
    },

    setSelectedContact(state, action) {
      state.selectedContact = action.payload;
      state.selectedChatId = action.payload?.chatId || null;
    },

    resetSelectedContact(state) {
      state.selectedContact = null;
      state.selectedChatId = null;
      state.isUserSelected = false;
    },

    updateContactFromSocket: (state, action) => {
      const updatedRaw = action.payload;
      const updated = { ...updatedRaw, chatId: String(updatedRaw.chatId) };

      const now = Date.now();

      const index = state.contacts.findIndex((c) => String(c.chatId) === updated.chatId);

      const merged =
        index !== -1
          ? { ...state.contacts[index], ...updated, _clientUpdatedAt: now }
          : { ...updated, _clientUpdatedAt: now };

      if (index !== -1) state.contacts.splice(index, 1);
      state.contacts.unshift(merged);

      // hard dedupe
      const seen = new Set();
      state.contacts = state.contacts.filter((c) => {
        const id = String(c.chatId);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },

    changePage(state, action) {
      state.page = action.payload;
    },
    changelimit(state, action) {
      state.limit = action.payload;
    },
    resetTelegramPagination(state) {
      state.page = DEFAULT_TELEGRAM_STATE.page;
      state.limit = DEFAULT_TELEGRAM_STATE.limit;
    },
    changeIsUserSelected(state, action) {
      state.isUserSelected = action.payload;
    },
    changeIsRecAudioSelect(state, action) {
      state.isRecAudioSelect = action.payload;
    },
    changeAgentSearchtUser(state, action) {
      state.agentSearchItem = action.payload?.toLowerCase();
    },
  },

  extraReducers: (builder) => {
    // -----------------------------------------------------
    // GET BOT CONFIGS
    // -----------------------------------------------------
    builder.addCase(getTelegramBotConfigs.fulfilled, (state, { payload, meta }) => {
      state.totalCount = payload.count;
      state.botConfigs = payload.rows;
      state.loading = false;
    });
    builder.addCase(getTelegramBotConfigs.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getTelegramBotConfigs.rejected, (state) => {
      state.loading = false;
    });

    // -----------------------------------------------------
    // ADD BOT CONFIG
    // -----------------------------------------------------
    builder.addCase(addTelegramBotConfig.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addTelegramBotConfig.fulfilled, (state, { payload }) => {
      state.botConfigs.push(payload);
      state.totalCount += 1;
      state.loading = false;
    });
    builder.addCase(addTelegramBotConfig.rejected, (state) => {
      state.loading = false;
    });

    // -----------------------------------------------------
    // DELETE BOT CONFIG
    // -----------------------------------------------------
    builder.addCase(deleteTelegramBotConfig.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteTelegramBotConfig.fulfilled, (state, { payload }) => {
      state.botConfigs = state.botConfigs.filter((bot) => bot.id !== payload.id);
      state.totalCount = Math.max(0, state.totalCount - 1);
      state.loading = false;
    });
    builder.addCase(deleteTelegramBotConfig.rejected, (state) => {
      state.loading = false;
    });

    // -----------------------------------------------------
    // GET CONTACTS
    // -----------------------------------------------------
    builder.addCase(getTelegramContacts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTelegramContacts.fulfilled, (state, { payload, meta }) => {
      const page = meta?.arg?.page || 0;
      const limit = meta?.arg?.limit || 10;

      // normalize + dedupe incoming
      const incomingMap = new Map();
      for (const row of payload.rows || []) {
        const r = { ...row, chatId: String(row.chatId) };
        incomingMap.set(r.chatId, r);
      }
      const incoming = Array.from(incomingMap.values());

      // build map from existing (already includes socket-stamped items)
      const existingMap = new Map(state.contacts.map((c) => [String(c.chatId), c]));

      for (const row of incoming) {
        const id = row.chatId;
        const existing = existingMap.get(id);

        if (!existing) {
          existingMap.set(id, row);
          continue;
        }

        // ✅ keep socket-updated version if it’s newer than this API refresh
        if (existing._clientUpdatedAt) {
          existingMap.set(id, { ...row, ...existing }); // existing wins
        } else {
          existingMap.set(id, { ...existing, ...row }); // api can win
        }
      }

      if (page === 0) {
        // keep API ordering but keep existing fields when needed
        state.contacts = incoming.map((r) => existingMap.get(r.chatId));
      } else {
        const existingIds = new Set(state.contacts.map((c) => String(c.chatId)));
        for (const r of incoming) {
          if (!existingIds.has(r.chatId)) state.contacts.push(existingMap.get(r.chatId));
        }
      }

      state.hasMore = incoming.length === limit;
      state.loading = false;
    });
    builder.addCase(getTelegramContacts.rejected, (state) => {
      state.loading = false;
    });

    // -----------------------------------------------------
    // GET MESSAGES
    // -----------------------------------------------------
    builder.addCase(getTelegramMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTelegramMessages.fulfilled, (state, { payload }) => {
      state.messages = payload?.rows;
      state.loading = false;
    });
    builder.addCase(getTelegramMessages.rejected, (state) => {
      state.loading = false;
    });

    // -----------------------------------------------------
    // SEND MESSAGE
    // -----------------------------------------------------
    builder.addCase(sendTelegramMessage.pending, (state) => {
      state.messageSending = true;
    });
    builder.addCase(sendTelegramMessage.fulfilled, (state) => {
      state.messageSending = false;
    });
    builder.addCase(sendTelegramMessage.rejected, (state) => {
      state.messageSending = false;
    });

    builder.addCase(resetUnreadCount.fulfilled, (state, { payload }) => {
      if (payload?.data?.id) {
        const contactIndex = state.contacts.findIndex((c) => c.id === payload.data.id);

        if (contactIndex !== -1) {
          // ✅ Update unread count and timestamps (or any other fields)
          state.contacts[contactIndex].unreadMessages = payload.data.unreadMessages;
        }
      }
      state.loading = false;
    });
    builder.addCase(resetUnreadCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resetUnreadCount.rejected, (state, { payload }) => {
      state.loading = false;
    });
  },
});

export const {
  setSelectedBotConfig,
  setSelectedContact,
  resetSelectedContact,
  updateContactFromSocket,
  changePage,
  changelimit,
  resetTelegramPagination,
  changeIsUserSelected,
  changeIsRecAudioSelect,
  changeAgentSearchtUser,
} = telegramSlice.actions;

export default telegramSlice.reducer;
